import React, { ReactNode } from 'react';
import { Select, MenuItem, FormControl, InputAdornment, SelectChangeEvent } from '@mui/material';
import type { MenuProps } from '@mui/material';
import { buildClassName } from 'utils/build-class-name';

//#region Types
export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
}

export interface SelectDropdownProps<T extends string | number> {
  /** Available options */
  options: SelectOption<T>[];
  /** Currently selected value */
  value: T;
  /** Handler for value changes */
  onValueChange: (value: T) => void;
  /** Icon component to display on the left */
  leftIcon?: ReactNode;
  /** Whether to show the dropdown arrow icon */
  showDropdownIcon?: boolean;
  /** Visual variant */
  variant?: 'default' | 'pill' | 'minimal';
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Additional CSS class */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** When true, renders the selected option even when its value is an empty string (e.g. an "Any" option) */
  displayEmpty?: boolean;
  /** Overrides for the MUI menu popover (e.g. paper styling for the options list) */
  menuProps?: Partial<MenuProps>;
}
//#endregion

/**
 * SelectDropdown - A styled select dropdown with icon support
 * 
 * Atomic Level: Molecule
 * 
 * Features:
 * - Left icon slot
 * - Custom dropdown arrow
 * - Multiple variants: default, pill, minimal
 * - Multiple sizes: small, medium, large
 * - Full accessibility support
 * - Built on Material UI Select for enhanced features
 * 
 * @example
 * ```tsx
 * <SelectDropdown
 *   options={[
 *     { value: 'asc', label: 'Ascending' },
 *     { value: 'desc', label: 'Descending' },
 *   ]}
 *   value={sortOrder}
 *   onValueChange={setSortOrder}
 *   leftIcon={<SortIcon />}
 *   variant="pill"
 * />
 * ```
 */
export default function SelectDropdown<T extends string | number>({
  options,
  value,
  onValueChange,
  leftIcon,
  showDropdownIcon = true,
  variant = 'default',
  size = 'medium',
  ariaLabel,
  className,
  testId,
  disabled,
  displayEmpty,
  menuProps,
}: SelectDropdownProps<T>) {
  const handleChange = (event: SelectChangeEvent<T>) => {
    const newValue = event.target.value as T;
    onValueChange(newValue);
  };

  // Map custom sizes to MUI sizes, heights, font sizes, and icon sizes
  const sizeMap = {
    small: { muiSize: 'small' as const, minHeight: '32px', fontSize: '12px', iconSize: '12px' },
    medium: { muiSize: 'medium' as const, minHeight: '40px', fontSize: '14px', iconSize: '20px' },
    large: { muiSize: 'medium' as const, minHeight: '50px', fontSize: '16px', iconSize: '24px' },
  };

  const { muiSize, minHeight, fontSize, iconSize } = sizeMap[size];

  // Determine border radius based on variant
  const getBorderRadius = () => {
    if (variant === 'pill') return '100px';
    if (variant === 'minimal') return '0';
    return '4px'; // default
  };

  // Map custom variant to MUI variant
  const getMuiVariant = () => {
    if (variant === 'minimal') return 'standard';
    return 'outlined';
  };

  return (
    <FormControl
      className={buildClassName(className)}
      size={muiSize}
      variant={getMuiVariant()}
      data-testid={testId}
      fullWidth
      disabled={disabled}
    >
      <Select
        value={value}
        onChange={handleChange}
        aria-label={ariaLabel}
        displayEmpty={displayEmpty}
        MenuProps={menuProps}
        startAdornment={
          leftIcon ? (
            <InputAdornment position="start" sx={{ ml: '0', mr: '0', '& svg': { fontSize: iconSize }}}>
              {leftIcon}
            </InputAdornment>
          ) : undefined
        }
        IconComponent={showDropdownIcon ? undefined : () => null}
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontSize,
          color: 'black',
          borderRadius: getBorderRadius(),
          backgroundColor: 'white',
          boxShadow: '0 1px 4px 0 rgba(0, 0, 0, .01), 0 4px 8px 0 rgba(0, 0, 0, .02), 0 1px 12px 0 rgba(0, 0, 0, .12)',
          padding: '10px 14px',
          minHeight,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiSelect-select': {
            paddingRight: '0 !important',
            paddingLeft: '2px !important',
          },
          '& .MuiSvgIcon-root': {
            color: 'black',
            fontSize: iconSize,
          },
          
        }}
      >
        {options.map(option => (
          <MenuItem key={String(option.value)} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
